import { alpha } from '@material-ui/core/styles'
import { createMethylation } from './methylation'

// ----------------------------------------------------------------------
// Lidya Genomics brand palette (shared with lidyagenomics.com, see the landing
// repo's docs/design-spec.md §2). Every ramp keeps the lighter → darker keys the
// rest of the app reads, so components pick the new colours up without changes.

function createGradient(color1, color2) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`
}

// Neutral greys. The first ramp was derived from the olive ink and carried a green
// cast that made dark mode read greenish; these stay neutral in both modes.
const GREY = {
  0: '#FFFFFF',
  100: '#F8F8F7',
  200: '#F0F0EE',
  300: '#E2E2DF',
  400: '#C7C7C3',
  500: '#9A9A96',
  600: '#6C6C69',
  700: '#474745',
  800: '#262626',
  900: '#161616',
  500_8: alpha('#9A9A96', 0.08),
  500_12: alpha('#9A9A96', 0.12),
  500_16: alpha('#9A9A96', 0.16),
  500_24: alpha('#9A9A96', 0.24),
  500_32: alpha('#9A9A96', 0.32),
  500_48: alpha('#9A9A96', 0.48),
  500_56: alpha('#9A9A96', 0.56),
  500_80: alpha('#9A9A96', 0.8),
  400_8: alpha('#C7C7C3', 0.08),
  400_12: alpha('#C7C7C3', 0.12),
  400_16: alpha('#C7C7C3', 0.16),
  300_8: alpha('#E2E2DF', 0.08),
  200_8: alpha('#F0F0EE', 0.08),
  100_8: alpha('#F8F8F7', 0.08),
}

// Brand olive (the logo's band ramp) is the light-mode accent. Dark mode uses a
// clinical blue on slate surfaces: no brand green next to the clinical greens, and
// it still reads as a coloured, professional product rather than a greyscale one.
const PRIMARY_LIGHT = {
  lighter: '#E8EDDC',
  light: '#8B9B51',
  main: '#536339',
  dark: '#44522E',
  darker: '#2C361D',
  contrastText: '#FFFFFF',
}
const PRIMARY_DARK = {
  lighter: '#E3EEFA',
  light: '#A9CBF0',
  main: '#72A9E6',
  dark: '#4F86C4',
  darker: '#2B4E78',
  contrastText: '#0B1320',
}
// Steel blue for secondary accents (HPO, multisample and similar controls).
const SECONDARY_LIGHT = {
  lighter: '#DEE8F1',
  light: '#7FA3C2',
  main: '#3F6E94',
  dark: '#2C5070',
  darker: '#1A3249',
  contrastText: '#FFFFFF',
}
const SECONDARY_DARK = {
  lighter: '#E3EDF6',
  light: '#B3CEE6',
  main: '#8DB4D6',
  dark: '#5F87AA',
  darker: '#34526E',
  contrastText: '#0E100D',
}
// Status colours: mid-tones that read on both paper and night surfaces.
const INFO = {
  lighter: '#DCE9F7',
  light: '#7FB0E0',
  main: '#3276B6',
  dark: '#245C93',
  darker: '#133A61',
  contrastText: '#FFFFFF',
}
const SUCCESS = {
  lighter: '#E1F0DC',
  light: '#8CC98B',
  main: '#2F8540',
  dark: '#226530',
  darker: '#17461D',
  contrastText: '#FFFFFF',
}
const WARNING = {
  lighter: '#FBF0D2',
  light: '#EDC766',
  main: '#E0A526',
  dark: '#9A6700',
  darker: '#6B4700',
  contrastText: GREY[900],
}
const ERROR = {
  lighter: '#FBE3DE',
  light: '#E0826F',
  main: '#C0392B',
  dark: '#9B2C1F',
  darker: '#6A1A12',
  contrastText: '#FFFFFF',
}

const PURPLE = {
  lighter: '#E6E7F4',
  light: '#9A9FD0',
  main: '#7177B5',
  dark: '#4F5490',
  darker: '#33366A',
  contrastText: '#fff',
}

const CHART_COLORS = {
  violet: ['#7177B5', '#9A9FD0', '#C3C6E6', '#E3E4F3'],
  blue: ['#3B82C4', '#7FB0E0', '#B5D3EE', '#DCEAF7'],
  green: ['#536339', '#8B9B51', '#B9CF8A', '#DDE8C6'],
  yellow: ['#C6B765', '#D4C77A', '#E6DDA8', '#F3EED5'],
  red: ['#D14B3A', '#E0826F', '#EFB3A6', '#F8DDD6'],
}

// ----------------------------------------------------------------------
// Clinical classification scales. One source of truth for tables, filter chips,
// canvas tracks and popovers: P red → LP orange → VUS amber → LB teal-green → B green,
// with a neutral for "not classified". `text` is the tone to use for words on the
// page background, `bg` a tinted fill for pills.
const clinicalScale = (tones, isLight) => {
  const fill = isLight ? 0.12 : 0.18
  return Object.fromEntries(
    Object.entries(tones).map(([key, [main, text]]) => [key, { main, text, bg: alpha(main, fill) }]),
  )
}
const ACMG_LIGHT = {
  pathogenic: ['#C62828', '#A11F1F'],
  likelyPathogenic: ['#E0662B', '#A8461A'],
  vus: ['#D69E2E', '#835800'],
  likelyBenign: ['#3E9C84', '#23705C'],
  benign: ['#2E7D4F', '#1E5C38'],
  none: ['#989B90', '#55584F'],
}
const ACMG_DARK = {
  pathogenic: ['#EF6B6B', '#F48A84'],
  likelyPathogenic: ['#F08A5D', '#F5A480'],
  vus: ['#E3B341', '#EBC466'],
  likelyBenign: ['#6CC3A8', '#8DD4BD'],
  benign: ['#5DBB7A', '#86CF9B'],
  none: ['#8A8D82', '#B3B5AB'],
}
const createClinical = (isLight) => {
  const acmg = clinicalScale(isLight ? ACMG_LIGHT : ACMG_DARK, isLight)
  const conflicting = clinicalScale({ conflicting: isLight ? ['#7177B5', '#4F5490'] : ['#9A9FD0', '#B8BCE0'] }, isLight)
  return {
    acmg,
    // ClinVar significance tiers reuse the ACMG tones so the two columns read alike.
    clinvar: {
      pathogenic: acmg.pathogenic,
      likelyPathogenic: acmg.likelyPathogenic,
      uncertain: acmg.vus,
      likelyBenign: acmg.likelyBenign,
      benign: acmg.benign,
      conflicting: conflicting.conflicting,
      other: acmg.none,
    },
  }
}

// Analysis kind accents: germline results use the primary accent (olive in light,
// blue in dark), somatic results use violet (orchid in dark) so the two are told
// apart by colour, not only by label.
const SOMATIC_LIGHT = {
  lighter: '#E6E7F4',
  light: '#9A9FD0',
  main: '#5F66A8',
  dark: '#474D86',
  darker: '#2F335C',
  contrastText: '#FFFFFF',
}
const SOMATIC_DARK = {
  lighter: '#F6E9F9',
  light: '#E6C3EE',
  main: '#D39BE0',
  dark: '#AE72BE',
  darker: '#6E3E7C',
  contrastText: '#16101A',
}

const createCommon = (primary) => ({
  common: { black: '#000', white: '#fff' },
  info: { ...INFO },
  success: { ...SUCCESS },
  warning: { ...WARNING },
  error: { ...ERROR },
  purple: { ...PURPLE },
  grey: GREY,
  gradients: {
    primary: createGradient(primary.light, primary.main),
    info: createGradient(INFO.light, INFO.main),
    success: createGradient(SUCCESS.light, SUCCESS.main),
    warning: createGradient(WARNING.light, WARNING.main),
    error: createGradient(ERROR.light, ERROR.main),
  },
  chart: CHART_COLORS,
})

const ACTION = {
  hover: GREY[500_8],
  selected: GREY[500_16],
  disabled: GREY[500_80],
  disabledBackground: GREY[500_24],
  focus: GREY[500_24],
  hoverOpacity: 0.08,
  disabledOpacity: 0.48,
}

const palette = {
  light: {
    ...createCommon(PRIMARY_LIGHT),
    ...createClinical(true),
    methylation: createMethylation(true),
    primary: { ...PRIMARY_LIGHT },
    somatic: { ...SOMATIC_LIGHT },
    secondary: { ...SECONDARY_LIGHT },
    // Crisp clinical light mode: white surfaces on a cool grey page, near-black ink
    // (about 18:1) and secondary text that stays dark at table sizes (about 10:1).
    // The earlier warm-paper version read too soft for dense data.
    divider: alpha('#0E1116', 0.14),
    text: { primary: '#0E1116', secondary: '#383D45', disabled: '#666C75' },
    background: { paper: '#FFFFFF', default: '#EEF0F3', neutral: '#E9ECF0' },
    action: { active: '#383D45', ...ACTION },
  },
  dark: {
    ...createCommon(PRIMARY_DARK),
    ...createClinical(false),
    methylation: createMethylation(false),
    primary: { ...PRIMARY_DARK },
    somatic: { ...SOMATIC_DARK },
    secondary: { ...SECONDARY_DARK },
    // Slate surfaces with a slight blue cast and cool neutral text (the first night
    // palette was green-black to match the website and read as a green tint).
    divider: alpha('#C8D4E3', 0.1),
    text: { primary: '#E9EDF2', secondary: '#B3BCC7', disabled: '#8791A0' },
    background: { paper: '#161B21', default: '#0F1318', neutral: alpha('#C8D4E3', 0.06) },
    action: { active: '#B3BCC7', ...ACTION },
  },
}

export default palette
